import type { MemoResult } from '@creanizer/types'
import { StickyNote, Pin, Hash } from 'lucide-react'
import { getStylesForType } from '../utils/visualStyles'
import '../styles/documentStyles.css'

interface MemoViewerProps {
  document: MemoResult
  visualStyle?: string
}

export function MemoViewer({ document, visualStyle = 'sticky' }: MemoViewerProps) {
  // データ検証
  if (!document || !document.content || !document.date) {
    return (
      <div className="p-6 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">メモ</h3>
        <pre className="text-sm text-gray-700 whitespace-pre-wrap">
          {JSON.stringify(document, null, 2)}
        </pre>
      </div>
    )
  }
  
  // ランダムな回転角度を生成（一貫性のため文字列から）
  const getRotation = (str: string): number => {
    const hash = str.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    return (hash % 7) - 3 // -3 to 3 degrees
  }
  
  // 付箋の色を決定
  const getStickyColor = (content: string): string => {
    const colors = [
      'from-yellow-200 to-yellow-300',
      'from-pink-200 to-pink-300',
      'from-blue-200 to-blue-300',
      'from-green-200 to-green-300',
      'from-purple-200 to-purple-300',
    ]
    const hash = content.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    return colors[hash % colors.length]
  }
  
  // ビジュアルスタイルの取得
  const styles = getStylesForType('memo')
  const selectedStyle = styles.find(s => s.id === visualStyle) || styles[0]
  const styleClass = selectedStyle.className
  
  // スタイル別のレンダリング
  if (visualStyle === 'notebook') {
    // ノートスタイル
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className={`p-8 rounded ${styleClass}`}>
          {document.title && (
            <h3 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">
              {document.title}
            </h3>
          )}
          <div className="text-sm text-gray-600 mb-4">{document.date}</div>
          <div className="space-y-2 text-gray-800">
            {document.content.split('\n').map((line, index) => (
              <div key={index}>
                {line.trim() ? <p>{line}</p> : <br />}
              </div>
            ))}
          </div>
          {document.tags && document.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">
              {document.tags.map((tag, index) => (
                <span key={index} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }
  
  if (visualStyle === 'torn') {
    // 破れたメモスタイル
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className={`p-8 relative ${styleClass}`}>
          {document.title && (
            <h3 className="text-lg font-bold mb-4 text-gray-900">
              {document.title}
            </h3>
          )}
          <div className="text-sm text-gray-700 mb-4">{document.date}</div>
          <div className="space-y-2 text-gray-900">
            {document.content.split('\n').map((line, index) => (
              <div key={index}>
                {line.trim() ? <p>{line}</p> : <br />}
              </div>
            ))}
          </div>
          {document.tags && document.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">
              {document.tags.map((tag, index) => (
                <span key={index} className="text-gray-600 text-sm">
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }
  
  if (visualStyle === 'bloodstained') {
    // 血痕のメモスタイル
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className={`p-8 relative ${styleClass}`}>
          {document.title && (
            <h3 className="text-xl font-bold mb-4 text-red-950" style={{ fontFamily: 'Courier New, monospace' }}>
              {document.title}
            </h3>
          )}
          <div className="text-sm text-red-900/70 mb-4">{document.date}</div>
          <div className="space-y-2 text-red-950/90">
            {document.content.split('\n').map((line, index) => (
              <div key={index} style={{
                transform: `rotate(${Math.random() * 0.3 - 0.15}deg)`,
                textShadow: '0 0 1px rgba(185, 28, 28, 0.2)'
              }}>
                {line.trim() ? <p>{line}</p> : <br />}
              </div>
            ))}
          </div>
          {document.tags && document.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">
              {document.tags.map((tag, index) => (
                <span key={index} className="text-red-800/70 text-sm">
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }
  
  // デフォルト（付箋スタイル）
  return (
    <div className="max-w-2xl mx-auto p-4">
      <div 
        className={`relative bg-gradient-to-br ${getStickyColor(document.content)} shadow-lg`}
        style={{
          transform: `rotate(${getRotation(document.content)}deg)`,
          minHeight: '300px'
        }}
      >
        {/* ピン留め装飾 */}
        <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
          <Pin className="w-8 h-8 text-red-500 fill-current drop-shadow-md" />
        </div>
        
        {/* メモ内容 */}
        <div className="p-8 pt-10">
          {/* タイトル */}
          {document.title && (
            <div className="mb-4 pb-2 border-b-2 border-gray-400/30">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <StickyNote className="w-5 h-5" />
                {document.title}
              </h2>
            </div>
          )}
          
          {/* 日付 */}
          <div className="text-sm text-gray-600 mb-4">
            {document.date}
          </div>
          
          {/* メモ本文 */}
          <div className="space-y-2">
            {document.content.split('\n').map((line, index) => (
              <div key={index}>
                {line.startsWith('・') || line.startsWith('- ') || line.startsWith('* ') ? (
                  <div className="flex items-start gap-2">
                    <span className="text-gray-600 mt-1">•</span>
                    <span className="text-gray-800">{line.replace(/^[・\-\*]\s*/, '')}</span>
                  </div>
                ) : line.startsWith('[ ]') || line.startsWith('[x]') ? (
                  <div className="flex items-start gap-2">
                    <input 
                      type="checkbox" 
                      checked={line.startsWith('[x]')}
                      readOnly
                      className="mt-1"
                    />
                    <span className="text-gray-800">{line.replace(/^\[[x ]\]\s*/, '')}</span>
                  </div>
                ) : line.trim() ? (
                  <p className="text-gray-800">{line}</p>
                ) : (
                  <br />
                )}
              </div>
            ))}
          </div>
          
          {/* タグ */}
          {document.tags && document.tags.length > 0 && (
            <div className="mt-6 pt-4 border-t border-gray-400/30">
              <div className="flex items-center gap-2 flex-wrap">
                <Hash className="w-4 h-4 text-gray-600" />
                {document.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-white/50 text-gray-700 text-sm rounded"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* 影の装飾 */}
        <div 
          className="absolute inset-0 bg-black/10 -z-10"
          style={{
            transform: 'translate(3px, 3px)',
            filter: 'blur(2px)'
          }}
        />
      </div>
      
      {/* 追加の装飾（テープ風） */}
      <div className="relative mt-4">
        <div className="absolute -top-8 left-4 w-20 h-8 bg-gray-200/50 transform -rotate-12 shadow-sm" />
        <div className="absolute -top-8 right-4 w-20 h-8 bg-gray-200/50 transform rotate-12 shadow-sm" />
      </div>
    </div>
  )
}